/**
 * Homepage iCarsoft Tool Finder
 * 2-step flow: vehicle type -> brand/make -> recommended products.
 * Product cards still use Liquid-rendered Shopify product JSON from the section,
 * so price, image, URL, variant ID and availability stay market-aware.
 */
(() => {
  if (customElements.get('icarsoft-tool-finder')) return;

  const P = {
    i800: 'i800',
    ultraP: 'cr-ultra-p',
    ultra: 'cr-ultra',
    eagleP: 'cr-eagle-p',
    eagle: 'icarsoft-cr-eagle',
    legend: 'cr-legend',
    unicorn: 'cr-unicorn',
    unicornS: 'icarsoft-cr-unicorn-for-passenger-cars',
    maxP: 'icarsoft-cr-max-p',
    maxBT: 'cr-max-bt',
    max: 'cr-max',
    proS: 'icarsoft-cr-pro-s',
    eliteP: 'icarsoft-cr-elite-p',
    hd40: 'icarsoft-hd-v4-0-multi-system-heavy-duty-vehicles-diagnostic-tool',
    hd30: 'hd-v3-0',
    hd2000: 'hd2000-heavy-duty-truck-package',
    mtPro: 'professional-multi-systems-motorcycle-diagnostic-tool-mt-pro',
    mtMini: 'mt-pro-mini',
    mtV6p: 'mt-v6-plus',
    x9: 'marinescan-x9'
  };

  const CAR_GROUPS = {
    mb: ['icarsoft-mb-v4-0', 'icarsoft-de-v4-0', P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    por: ['icarsoft-por-v4-0', 'icarsoft-de-v4-0', P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    bmm: ['icarsoft-bmm-v4-0', 'icarsoft-de-v4-0', P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    vaws: ['icarsoft-vaws-v4-0', 'icarsoft-de-v4-0', P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    lr: ['icarsoft-lr-v4-0', P.eliteP, P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    vol: ['icarsoft-vol-v4-0', P.eliteP, P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    op: ['icarsoft-op-v4-0', P.eliteP, P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    us: ['icarsoft-us-v4-0', P.eliteP, P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    jp: ['icarsoft-jp-v4-0', P.eliteP, P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP],
    fr: ['icarsoft-fr-v4-0', P.eliteP, P.proS, P.max, P.maxBT, P.maxP, P.eagle, P.ultraP]
  };

  const CAR_GROUP_BY_MAKE = {
    'Mercedes-Benz': 'mb',
    Maybach: 'mb',
    Smart: 'mb',
    Sprinter: 'mb',
    Porsche: 'por',
    BMW: 'bmm',
    Mini: 'bmm',
    'Rolls-Royce': 'bmm',
    Audi: 'vaws',
    Bentley: 'vaws',
    Bugatti: 'vaws',
    Lamborghini: 'vaws',
    Seat: 'vaws',
    Skoda: 'vaws',
    Volkswagen: 'vaws',
    VW: 'vaws',
    'VW CV': 'vaws',
    'VW LT3': 'vaws',
    'Man LD': 'vaws',
    'Land Rover': 'lr',
    Jaguar: 'lr',
    Volvo: 'vol',
    SAAB: 'vol',
    Saab: 'vol',
    Opel: 'op',
    Vauxhall: 'op',
    Chrysler: 'us',
    Dodge: 'us',
    Ford: 'us',
    GM: 'us',
    Chevrolet: 'us',
    Jeep: 'us',
    Holden: 'us',
    Daewoo: 'jp',
    Abarth: 'fr',
    Fiat: 'fr',
    Lancia: 'fr',
    'Alfa Romeo': 'fr',
    Ferrari: 'fr',
    Maserati: 'fr',
    Citroen: 'fr',
    'Citroen / DS': 'fr',
    DS: 'fr',
    Peugeot: 'fr',
    Dacia: 'fr',
    Renault: 'fr',
    'Renault-Samsung': 'fr',
    Acura: 'jp',
    Honda: 'jp',
    Hyundai: 'jp',
    KIA: 'jp',
    Kia: 'jp',
    Isuzu: 'jp',
    Mazda: 'jp',
    Mitsubishi: 'jp',
    Infiniti: 'jp',
    Nissan: 'jp',
    'Nissan GTR': 'jp',
    'Nissan GT-R': 'jp',
    Subaru: 'jp',
    Suzuki: 'jp',
    Lexus: 'jp',
    Scion: 'jp',
    Toyota: 'jp'
  };

  const EXTENDED_CAR_MAKES = [
    'Aston Martin', 'Buick', 'Cadillac', 'Lincoln', 'Rover', 'Daihatsu', 'SsangYong',
    'Proton', 'Perodua', 'Tata', 'Mahindra', 'Maruti', 'IVECO', 'Luxgen', 'Qoros',
    'Maxus', 'SAIC MG', 'SGM', 'JMC', 'Foton', 'Great Wall', 'Brilliance', 'BAIC',
    'Changan', 'Changhe', 'Chery', 'Hafei', 'King Long', 'Joylong', 'Lifan',
    'Trumpchi', 'GAZ', 'UAZ', 'PAZ', 'IKCO', 'MVM', 'CMC'
  ];

  const CAR_MAKES = Object.keys(CAR_GROUP_BY_MAKE)
    .filter((make, index, list) => list.findIndex((item) => normalize(item) === normalize(make)) === index)
    .concat(EXTENDED_CAR_MAKES)
    .concat(['Other make']);

  const CAR_DEFAULT = [P.eagleP, P.maxP, P.ultraP, P.ultra, P.unicorn, P.unicornS, P.legend, P.i800];
  const CAR_FALLBACK = [P.i800, P.ultra, P.ultraP, P.unicorn, P.unicornS, P.legend];
  const SHOWCASE_FALLBACKS = {
    new: [P.eagleP, P.maxP, P.ultraP, P.proS],
    hot: ['icarsoft-por-v4-0', 'icarsoft-mb-v4-0', 'icarsoft-lr-v4-0', 'icarsoft-de-v4-0'],
    deals: [P.maxBT, P.eagle, P.mtV6p, P.hd40]
  };

  const MOTO_MAKES = [
    'Harley-Davidson', 'Honda', 'Yamaha', 'BMW', 'Suzuki', 'Kawasaki', 'Ducati', 'KTM',
    'Triumph', 'Indian', 'Vespa', 'Piaggio', 'Aprilia', 'Moto Guzzi', 'Polaris', 'Victory',
    'Enfield', 'Benelli', 'QJBenelli', 'CFMOTO', 'Kymco', 'SYM', 'Hyosung', 'Bajaj', 'TVS',
    'Peugeot', 'Derbi', 'Gilera', 'Haojue', 'Hartford', 'HM', 'Aeon', 'PGO', 'Royal Alloy',
    'Shineray', 'Zontes', 'Other brand'
  ];

  const MT_V6 = {
    'Harley-Davidson': 'icarsoft-mt-v6-for-harley-davidson',
    BMW: 'mtv6-bmw',
    Suzuki: 'icarsoft-motorcycle-diagnostic-tool-mt-v6-for-suzuki',
    Kawasaki: 'icarsoft-motorcycle-diagnostic-tool-mt-v6-for-kawasaki',
    Indian: 'icarsoft-motorcycle-diagnostic-tool-mt-v6-for-indian',
    Honda: 'mt-v6-for-honda',
    Yamaha: 'mt-v6-for-yamaha',
    Ducati: 'mt-v6-for-ducati',
    KTM: 'mt-v6-for-ktm',
    Triumph: 'mt-v6-for-triumph',
    Vespa: 'mt-v6-for-vespa',
    Piaggio: 'mt-v6-for-piaggio',
    Aprilia: 'mt-v6-for-aprilia',
    'Moto Guzzi': 'mt-v6-for-moto-guzzi',
    Polaris: 'mt-v6-for-polaris',
    Victory: 'mt-v6-for-victory',
    Enfield: 'mt-v6-for-enfield',
    Benelli: 'mt-v6-for-benelli',
    QJBenelli: 'mt-v6-for-qjbenelli',
    CFMOTO: 'mt-v6-for-cfmoto',
    Kymco: 'mt-v6-for-kymco',
    SYM: 'mt-v6-for-sym',
    Hyosung: 'mt-v6-for-hyosung',
    Bajaj: 'mt-v6-for-bajaj',
    TVS: 'mt-v6-for-tvs',
    Peugeot: 'mt-v6-for-peugeot',
    Derbi: 'mt-v6-for-derbi',
    Gilera: 'mt-v6-for-gilera',
    Haojue: 'mt-v6-for-haojue',
    Hartford: 'mt-v6-for-hartford',
    HM: 'mt-v6-for-hm',
    Aeon: 'mt-v6-for-aeon',
    PGO: 'mt-v6-for-pgo',
    'Royal Alloy': 'mt-v6-for-royal-alloy',
    Shineray: 'mt-v6-for-shineray',
    Zontes: 'mt-v6-for-zontes'
  };

  const VEHICLE_OPTS = [
    { key: 'car', label: 'Passenger Car', ico: '<path d="M5 13l1.5-4.5A2 2 0 018.4 7h7.2a2 2 0 011.9 1.5L19 13M5 13h14v4H5zM7 17v2M17 17v2"/><circle cx="8" cy="15" r=".6"/><circle cx="16" cy="15" r=".6"/>' },
    { key: 'moto', label: 'Motorcycle', ico: '<circle cx="6" cy="16" r="3"/><circle cx="18" cy="16" r="3"/><path d="M6 16l4-6h5l2 6M10 10l-1-2H7"/>' },
    { key: 'truck', label: 'Heavy Duty', ico: '<path d="M3 7h11v8H3zM14 10h4l3 3v2h-7zM3 15v2M7 17a1.5 1.5 0 103 0M17 17a1.5 1.5 0 103 0"/>' },
    { key: 'marine', label: 'Marine', ico: '<path d="M4 17c1.3 1.1 2.7 1.1 4 0s2.7-1.1 4 0 2.7 1.1 4 0 2.7-1.1 4 0M6 14l1.2-5H14l3 5M10 9V5h2.5v4"/>' }
  ];

  const TAGS = {
    [P.i800]: 'Generic OBD2 / EOBD / CAN engine diagnostics',
    [P.ultraP]: 'Professional platform / topology / programming workflows',
    [P.ultra]: 'Professional scanner for deeper workshop diagnostics',
    [P.eagleP]: '2026 flagship all-in-one diagnostic platform',
    [P.eagle]: 'Professional bi-directional all-systems scanner',
    [P.legend]: 'Broad vehicle coverage with CAN FD and DoIP support',
    [P.unicorn]: 'OE-level professional car diagnostic platform',
    [P.unicornS]: 'OE-level car and commercial vehicle platform',
    [P.maxP]: 'Bi-directional diagnostic tablet for everyday workshops',
    [P.maxBT]: 'Wireless bi-directional diagnostics for supported vehicles',
    [P.max]: 'All-system diagnostics and service reset workflows',
    [P.proS]: 'Bi-directional OBD2 scanner for practical workshop use',
    [P.eliteP]: 'Touchscreen, Wi-Fi and advanced full-system coverage',
    [P.hd40]: 'Multi-system heavy-duty truck diagnostic tool',
    [P.hd30]: 'Heavy-duty code reader for truck diagnostics',
    [P.hd2000]: 'Heavy-duty expansion package for CR Ultra',
    [P.mtPro]: 'Multi-brand professional motorcycle diagnostic scanner',
    [P.mtMini]: 'Compact multi-brand motorcycle scanner',
    [P.mtV6p]: 'Multi-brand motorcycle diagnostic coverage',
    [P.x9]: 'Marine engine diagnostic scanner',
    'icarsoft-mb-v4-0': 'Single-brand V4.0 scanner for Mercedes-Benz family',
    'icarsoft-de-v4-0': 'German vehicle make coverage',
    'icarsoft-por-v4-0': 'Single-brand V4.0 scanner for Porsche',
    'icarsoft-bmm-v4-0': 'Single-brand V4.0 scanner for BMW / MINI family',
    'icarsoft-vaws-v4-0': 'Single-brand V4.0 scanner for VW / Audi group',
    'icarsoft-lr-v4-0': 'Single-brand V4.0 scanner for Land Rover / Jaguar',
    'icarsoft-vol-v4-0': 'Single-brand V4.0 scanner for Volvo / SAAB',
    'icarsoft-op-v4-0': 'Single-brand V4.0 scanner for Opel / Vauxhall',
    'icarsoft-us-v4-0': 'Single-brand V4.0 scanner for American makes',
    'icarsoft-jp-v4-0': 'Single-brand V4.0 scanner for Japanese and Korean makes',
    'icarsoft-fr-v4-0': 'Single-brand V4.0 scanner for French and Italian makes'
  };

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/gi, '')
      .toLowerCase();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[ch]));
  }

  const PRODUCT_CARD_IMAGE_WIDTHS = [180, 240, 320, 360, 480, 540, 560, 640, 720, 800, 900];
  const PRODUCT_CARD_IMAGE_QUALITY = 1.6;
  const PRODUCT_CARD_IMAGE_SIZES = '(min-width: 1576px) 354px, (min-width: 1200px) calc((100vw - 162px) / 4), (min-width: 750px) calc((100vw - 118px) / 2), calc((100vw - 44px) / 2)';

  function imageUrlAtWidth(value, width) {
    const source = String(value || '');
    if (!source) return '';
    if (/([?&])width=\d+/i.test(source)) {
      return source.replace(/([?&])width=\d+/i, `$1width=${width}`);
    }
    return `${source}${source.includes('?') ? '&' : '?'}width=${width}`;
  }

  function productCardImageMarkup(product, loadImmediately = false) {
    if (!product || !product.image) return '<span class="ictf__placeholder">iCarsoft</span>';
    const declaredWidth = Number(product.imageWidth);
    const declaredHeight = Number(product.imageHeight);
    const sourceWidth = Number.isFinite(declaredWidth) && declaredWidth > 0 ? Math.round(declaredWidth) : 1200;
    const sourceHeight = Number.isFinite(declaredHeight) && declaredHeight > 0 ? Math.round(declaredHeight) : sourceWidth;
    const maxSourceWidth = Math.min(sourceWidth, 1440);
    const maxDisplayWidth = Math.max(1, Math.floor(maxSourceWidth / PRODUCT_CARD_IMAGE_QUALITY));
    const widths = PRODUCT_CARD_IMAGE_WIDTHS.filter((width) => width < maxDisplayWidth);
    if (!widths.length || widths[widths.length - 1] !== maxDisplayWidth) widths.push(maxDisplayWidth);
    const fallbackWidth = Math.min(maxSourceWidth, 720);
    const fallbackHeight = Math.max(1, Math.round((fallbackWidth * sourceHeight) / sourceWidth));
    const src = imageUrlAtWidth(product.image, fallbackWidth);
    const srcset = widths
      .map((width) => {
        const requestWidth = Math.min(maxSourceWidth, Math.round(width * PRODUCT_CARD_IMAGE_QUALITY));
        return `${imageUrlAtWidth(product.image, requestWidth)} ${width}w`;
      })
      .join(', ');
    const loading = loadImmediately ? 'eager' : 'lazy';
    const fetchPriority = loadImmediately ? ' fetchpriority="low"' : '';
    return `<img class="ictf__img" src="${escapeHtml(src)}" srcset="${escapeHtml(srcset)}" sizes="${PRODUCT_CARD_IMAGE_SIZES}" alt="${escapeHtml(product.title)}" loading="${loading}"${fetchPriority} decoding="async" width="${fallbackWidth}" height="${fallbackHeight}">`;
  }

  function moneyMarkup(value, className = '') {
    return `<span class="money${className ? ` ${className}` : ''}">${escapeHtml(value)}</span>`;
  }

  let currencyRefreshFrame = 0;
  function refreshCurrency() {
    window.cancelAnimationFrame(currencyRefreshFrame);
    currencyRefreshFrame = window.requestAnimationFrame(() => {
      if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') window.bucksCC.reConvert();
    });
  }

  function shortName(title) {
    return String(title || '')
      .replace(/^iCarsoft\s+/i, '')
      .replace(/\s+Advanced Automotive Diagnostic Tool.*$/i, '')
      .replace(/\s+Car Diagnostic Tool.*$/i, '')
      .replace(/\s+Automotive Car Diagnostic Tool.*$/i, '')
      .replace(/\s+OBDII\/EOBD\/CAN Auto Diagnostic Tool.*$/i, ' i800 Plus')
      .replace(/\s+Motorcycle Diagnostic Tool(?=\s+for\s)/i, '')
      .trim();
  }

  function compareName(item) {
    const labels = {
      [P.i800]: 'i800',
      [P.ultraP]: 'CR Ultra P',
      [P.ultra]: 'CR Ultra',
      [P.eagleP]: 'CR Eagle P',
      [P.eagle]: 'CR Eagle',
      [P.legend]: 'CR Legend',
      [P.unicorn]: 'CR Unicorn',
      [P.unicornS]: 'CR Unicorn S',
      [P.maxP]: 'CR Max P',
      [P.maxBT]: 'CR Max BT',
      [P.max]: 'CR Max',
      [P.proS]: 'CR Pro S',
      [P.eliteP]: 'CR Elite P',
      [P.hd40]: 'HD V4.0',
      [P.hd30]: 'HD V3.0',
      [P.hd2000]: 'HD2000',
      [P.mtPro]: 'MT Pro',
      [P.mtMini]: 'MT Pro Mini',
      [P.mtV6p]: 'MT V6 Plus',
      [P.x9]: 'MarineScan X9'
    };
    const handle = item && item.handle;
    return labels[handle] || shortName(item && item.title);
  }

  function compareHeader(item) {
    const name = compareName(item);
    const image = item && item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(name)}" loading="lazy">`
      : '';
    return `<th><span class="ictf__compare-product">${image}<span class="ictf__compare-product-name">${escapeHtml(name)}</span></span></th>`;
  }

  function moneyNumber(cents) {
    const n = Number(cents || 0);
    return Number.isFinite(n) ? n : 0;
  }

  function savingPercent(product) {
    const price = moneyNumber(product && product.cents);
    const compare = moneyNumber(product && product.compareCents);
    if (!price || !compare || compare <= price) return 0;
    return Math.round(((compare - price) / compare) * 100);
  }

  function categoryFor(handle) {
    if (handle === P.mtPro || handle === P.mtMini || handle === P.mtV6p || /^mt-v6|^icarsoft-mt-v6|^mtv6/i.test(handle)) return 'Motorcycle diagnostics';
    if (handle === P.hd40 || handle === P.hd30 || handle === P.hd2000) return 'Heavy-duty diagnostics';
    if (handle === P.x9) return 'Marine diagnostics';
    if (/v4-0|v3-0|v2-0|v1-0|por-ii|i980|i906|i900|p700/i.test(handle)) return 'Single-brand scanner';
    if (handle === P.i800) return 'Entry OBDII scanner';
    return 'All systems / OE-level';
  }

  function specsFor(handle) {
    const shared = window.ICARSOFT_COMPARE_SPECS;
    if (shared) {
      const spec = shared.specFor({ handle }, 'finder');
      if (spec) return spec;
    }
    const isMtV6 = /^mt-v6|^icarsoft-mt-v6|^mtv6/i.test(handle);
    const map = {
      [P.i800]: { coverage: 'Generic OBDII / EOBD / CAN', updates: 'USB update', highlights: 'Engine fault-code diagnostics' },
      [P.ultraP]: { coverage: 'OE-level professional coverage', updates: '3-year free updates', highlights: 'Topology mapping / J2534 / programming' },
      [P.ultra]: { coverage: 'OE-level professional coverage', updates: 'USB update', highlights: 'Advanced diagnostics and coding' },
      [P.eagleP]: { coverage: 'All systems / OE-level', updates: 'Lifetime Wi-Fi updates', highlights: 'TPMS programming / ECU coding / J2534' },
      [P.eagle]: { coverage: 'All systems / OE-level', updates: 'Lifetime Wi-Fi updates', highlights: 'ECU coding / TPMS / active tests' },
      [P.legend]: { coverage: 'All systems / OE-level', updates: 'Lifetime Wi-Fi updates', highlights: 'CAN FD / DoIP / broad coverage' },
      [P.unicorn]: { coverage: 'OE-level professional platform', updates: 'Wi-Fi update', highlights: 'Passenger-car workshop diagnostics' },
      [P.unicornS]: { coverage: 'Car and truck professional platform', updates: 'Wi-Fi update', highlights: 'Passenger + supported heavy-duty coverage' },
      [P.maxP]: { coverage: 'All systems / OE-level', updates: 'Lifetime Wi-Fi updates', highlights: 'Bi-directional control / ECU coding' },
      [P.maxBT]: { coverage: 'All systems / OE-level', updates: 'Lifetime Wi-Fi updates', highlights: 'Wireless bi-directional diagnostics' },
      [P.max]: { coverage: 'All systems', updates: 'USB update', highlights: 'Bi-directional tests / service functions' },
      [P.proS]: { coverage: 'All systems', updates: 'USB update', highlights: 'Bi-directional control / service resets' },
      [P.eliteP]: { coverage: 'All systems / OE-level', updates: 'Lifetime Wi-Fi updates', highlights: 'Touchscreen / service functions' },
      [P.hd40]: { coverage: 'Heavy-duty truck systems', updates: 'Wi-Fi software updates', highlights: 'Commercial vehicle diagnostics' },
      [P.hd30]: { coverage: 'Heavy-duty code reader', updates: 'USB update', highlights: 'Portable truck fault-code diagnostics' },
      [P.hd2000]: { coverage: 'Heavy-duty expansion package', updates: 'Adapter package', highlights: 'Works with CR Ultra host tool' },
      [P.mtPro]: { coverage: 'Most motorcycle brands', updates: 'USB update', highlights: 'Professional multi-brand MT diagnostics' },
      [P.mtMini]: { coverage: 'Most motorcycles', updates: 'USB update', highlights: 'Compact multi-brand motorcycle scanner' },
      [P.mtV6p]: { coverage: 'Most motorcycles', updates: 'USB update', highlights: 'Essential motorcycle systems' },
      [P.x9]: { coverage: 'Marine engine diagnostics', updates: 'USB update', highlights: 'Marine scan workflow' }
    };
    if (map[handle]) return map[handle];
    if (isMtV6) return { coverage: 'Dedicated motorcycle brand', updates: 'USB update', highlights: 'Single-brand MT V6 diagnostics' };
    if (/v4-0/i.test(handle)) return { coverage: 'Dedicated vehicle make coverage', updates: 'Lifetime Wi-Fi updates', highlights: 'Full-system V4.0 diagnostics' };
    if (/v3-0/i.test(handle)) return { coverage: 'Dedicated vehicle make coverage', updates: 'USB update', highlights: 'Full-system diagnostics + active tests' };
    if (/v2-0/i.test(handle)) return { coverage: 'Dedicated vehicle make coverage', updates: 'USB update', highlights: 'Service reset and system diagnostics' };
    if (/v1-0|por-ii|i980|i906|i900|p700/i.test(handle)) return { coverage: 'Dedicated vehicle make coverage', updates: 'USB update', highlights: 'Essential system diagnostics' };
    return { coverage: 'Recommended coverage', updates: 'Confirm update method', highlights: TAGS[handle] || 'Confirm exact compatibility by VIN' };
  }

  function buyNowUrl(product) {
    return product && product.variant ? `/cart/${encodeURIComponent(product.variant)}:1?checkout` : (product && product.url) || '/cart';
  }

  function compareRows(items) {
    const shared = window.ICARSOFT_COMPARE_SPECS;
    if (!shared) {
      return [
        ['Price', (item) => item.price ? moneyMarkup(item.price) : '-'],
        ['Best for', (item) => categoryFor(item.handle)],
        ['Coverage', (item) => specsFor(item.handle).coverage],
        ['Updates', (item) => specsFor(item.handle).updates],
        ['Highlights', (item) => specsFor(item.handle).highlights],
        ['Product page', (item) => `<a href="${escapeHtml(item.url)}">View details</a>`]
      ];
    }
    const rows = shared.rowsFor('catalog').filter(([label]) => label !== 'Product type');
    return [
      ...rows.map(([label, key]) => [label, (item) => {
        const value = shared.value(shared.merge(item, 'finder'), key);
        return key === 'price' ? moneyMarkup(value) : escapeHtml(value);
      }]),
      ['Product page', (item) => `<a href="${escapeHtml(item.url)}">View details</a>`]
    ];
  }

  function uniqueExisting(handles, products) {
    const seen = new Set();
    return handles.filter((handle) => {
      if (!handle || seen.has(handle) || !products[handle]) return false;
      seen.add(handle);
      return true;
    });
  }

  function parseHandleList(value, fallback) {
    const handles = String(value || '')
      .split(/[\s,]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    return handles.length ? handles : fallback;
  }

  function findCarGroup(make) {
    const key = normalize(make);
    const found = Object.keys(CAR_GROUP_BY_MAKE).find((item) => normalize(item) === key);
    return found ? CAR_GROUP_BY_MAKE[found] : null;
  }

  class ICarsoftToolFinder extends HTMLElement {
    connectedCallback() {
      try {
        this.products = JSON.parse(this.querySelector('[data-ictf-products]').textContent || '{}');
      } catch {
        this.products = {};
      }
      this.state = { vehicle: 'car', make: null, open: null };
      this.strings = {
        best: this.dataset.labelBest || 'BEST MATCH',
        also: this.dataset.labelAlso || 'ALSO FITS',
        buy: this.dataset.labelBuy || 'Buy',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        fitOne: this.dataset.labelFitOne || 'TOOL FITS YOUR SELECTION',
        fitMany: this.dataset.labelFitMany || 'TOOLS FIT YOUR SELECTION'
      };
      this.showcaseActive = 'new';
      this.showcaseTabs = [
        {
          key: 'new',
          label: this.dataset.showcaseLabelNew || 'New Arrivals',
          handles: parseHandleList(this.dataset.showcaseNew, SHOWCASE_FALLBACKS.new)
        },
        {
          key: 'hot',
          label: this.dataset.showcaseLabelHot || 'Best Sellers',
          handles: parseHandleList(this.dataset.showcaseHot, SHOWCASE_FALLBACKS.hot)
        },
        {
          key: 'deals',
          label: this.dataset.showcaseLabelDeals || 'Deals',
          handles: parseHandleList(this.dataset.showcaseDeals, SHOWCASE_FALLBACKS.deals)
        }
      ];
      this.selectedCompare = [];
      this.compareTray = this.querySelector('[data-ictf-compare-tray]');
      this.compareCount = this.querySelector('[data-ictf-compare-count]');
      this.compareThumbs = this.querySelector('[data-ictf-compare-thumbs]');
      this.compareOpen = this.querySelector('[data-ictf-compare-open]');
      this.compareClear = this.querySelector('[data-ictf-compare-clear]');
      this.compareModal = this.querySelector('[data-ictf-compare-modal]');
      this.compareTable = this.querySelector('[data-ictf-compare-table]');
      this.bindStaticEvents();
      this.render();
      this.scheduleDefaultImageWarmup();
    }

    scheduleDefaultImageWarmup() {
      if (this.defaultImageWarmupScheduled) return;
      this.defaultImageWarmupScheduled = true;
      const queueWarmup = () => {
        const warmup = () => {
          if (this.state.vehicle !== 'car' || this.state.make) return;
          this.querySelectorAll('[data-ictf-grid] .ictf__img[loading="lazy"]').forEach((image) => {
            image.setAttribute('loading', 'eager');
            image.setAttribute('fetchpriority', 'low');
          });
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(warmup, { timeout: 1000 });
        } else {
          window.setTimeout(warmup, 200);
        }
      };
      if (document.readyState === 'complete') queueWarmup();
      else window.addEventListener('load', queueWarmup, { once: true });
    }

    bindStaticEvents() {
      this.querySelectorAll('[data-ictf-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const n = Number(btn.dataset.ictfToggle);
          this.setState({ open: this.state.open === n ? null : n });
        });
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target) && this.state.open) this.setState({ open: null });
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.state.open) this.setState({ open: null });
        if (event.key === 'Escape' && this.compareModal && !this.compareModal.hidden) this.closeCompare();
      });
      this.compareOpen?.addEventListener('click', () => this.openCompare());
      this.compareClear?.addEventListener('click', () => this.clearCompare());
      this.querySelectorAll('[data-ictf-compare-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeCompare());
      });
      this.addEventListener('click', (event) => {
        const tab = event.target.closest?.('[data-ictf-showcase-tab]');
        if (!tab || !this.contains(tab)) return;
        this.showcaseActive = tab.dataset.ictfShowcaseTab || 'new';
        this.renderShowcase(true);
      });
    }

    setState(patch) {
      Object.assign(this.state, patch);
      this.render();
    }

    getBrandOptions() {
      if (this.state.vehicle === 'moto') return MOTO_MAKES;
      if (this.state.vehicle === 'truck') return ['All heavy-duty trucks'];
      if (this.state.vehicle === 'marine') return ['Marine diagnostic tools'];
      return CAR_MAKES;
    }

    stepConfig(n) {
      if (n === 1) {
        return { key: 'vehicle', opts: VEHICLE_OPTS, ph: 'Please Select Your Vehicle Type', search: false };
      }
      const brands = this.getBrandOptions().map((make) => ({
        key: make,
        label: (make === 'Other make' || make === 'Other brand' || make.startsWith('All ') || make.startsWith('Marine'))
          ? make
          : `<span class="ictf__for">for</span> ${escapeHtml(make)}`
      }));
      return { key: 'make', opts: brands, ph: 'Please Select Vehicle Brand', search: true };
    }

    getMatches() {
      let handles;
      if (this.state.vehicle === 'moto') {
        const dedicated = this.state.make && MT_V6[this.state.make] ? [MT_V6[this.state.make]] : [];
        handles = dedicated.concat([P.mtPro, P.mtMini, P.mtV6p]);
      } else if (this.state.vehicle === 'truck') {
        handles = [P.hd40, P.hd30, P.unicornS, P.hd2000];
      } else if (this.state.vehicle === 'marine') {
        handles = [P.x9];
      } else if (this.state.make) {
        const group = findCarGroup(this.state.make);
        handles = group ? CAR_GROUPS[group] : CAR_FALLBACK;
      } else {
        handles = CAR_DEFAULT;
      }
      return uniqueExisting(handles, this.products);
    }

    renderProductCard(handle, index, loadImmediately = false) {
      const product = this.products[handle];
      if (!product) return '';
      const best = index === 0;
      const specs = specsFor(handle);
      const save = savingPercent(product);
      const checked = this.selectedCompare.includes(handle);
      const priceHtml = product.compare && product.compare !== product.price
        ? `${moneyMarkup(product.price, 'ictf__money-current')} <s>${moneyMarkup(product.compare, 'ictf__money-compare')}</s>`
        : moneyMarkup(product.price || '', 'ictf__money-current');
      const cta = product.available
        ? `<a class="ictf__buy" href="${escapeHtml(buyNowUrl(product))}">Buy now</a>`
        : `<div class="ictf__sold-out">${escapeHtml(this.strings.soldOut)}</div>`;
      return `
        <article class="ictf__product-card${best ? ' is-best' : ''}" data-ictf-product-card="${escapeHtml(handle)}">
          <div class="ictf__product-media">
            <div class="ictf__badges">
              <span class="ictf__badge ictf__badge--red">${escapeHtml(best ? this.strings.best : this.strings.also)}</span>
              ${save ? `<span class="ictf__badge ictf__badge--dark">SAVE ${save}%</span>` : ''}
            </div>
            <a href="${escapeHtml(product.url)}" aria-label="${escapeHtml(product.title)}">
              ${productCardImageMarkup(product, loadImmediately)}
            </a>
            <label class="ictf__compare-control">
              <input type="checkbox" data-ictf-compare-toggle data-ictf-handle="${escapeHtml(handle)}"${checked ? ' checked' : ''}>
              <span>Compare</span>
            </label>
          </div>
          <div class="ictf__product-body">
            <p class="ictf__product-type">${escapeHtml(categoryFor(handle))}</p>
            <h3 class="ictf__product-title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
            <div class="ictf__product-price">${priceHtml}</div>
            <ul class="ictf__product-specs">
              <li><span>Coverage</span><b>${escapeHtml(specs.coverage)}</b></li>
              <li><span>Updates</span><b>${escapeHtml(specs.updates)}</b></li>
              <li><span>Highlights</span><b>${escapeHtml(specs.highlights)}</b></li>
            </ul>
            <div class="ictf__actions">
              <a class="ictf__button" href="${escapeHtml(product.url)}">View details</a>
              ${cta}
            </div>
          </div>
        </article>
      `;
    }

    bindCompareToggles(scope) {
      if (!scope) return;
      scope.querySelectorAll('[data-ictf-compare-toggle]').forEach((input) => {
        input.addEventListener('change', () => this.toggleCompare(input.dataset.ictfHandle, input.checked));
      });
    }

    renderShowcase(loadImmediately = false) {
      const showcase = this.querySelector('[data-ictf-showcase]');
      if (!showcase) return;
      const tabsWrap = this.querySelector('[data-ictf-showcase-tabs]');
      const grid = this.querySelector('[data-ictf-showcase-grid]');
      if (!tabsWrap || !grid) return;
      const active = this.showcaseTabs.find((tab) => tab.key === this.showcaseActive) || this.showcaseTabs[0];
      this.showcaseActive = active.key;
      tabsWrap.innerHTML = this.showcaseTabs.map((tab) => `
        <button type="button" class="ictf__showcase-tab${tab.key === active.key ? ' is-active' : ''}" data-ictf-showcase-tab="${escapeHtml(tab.key)}" role="tab" aria-selected="${tab.key === active.key ? 'true' : 'false'}">
          ${escapeHtml(tab.label)}
        </button>
      `).join('');
      const handles = uniqueExisting(active.handles, this.products).slice(0, 4);
      grid.innerHTML = handles.length
        ? handles.map((handle, index) => this.renderProductCard(handle, index, loadImmediately)).join('')
        : '<div class="ictf__showcase-empty">Add product handles in the theme editor to show this group.</div>';
      showcase.hidden = false;
      this.bindCompareToggles(grid);
      this.updateCompareTray();
      refreshCurrency();
    }

    renderStep(n) {
      const stepEl = this.querySelector(`[data-ictf-step="${n}"]`);
      if (!stepEl) return;
      const cfg = this.stepConfig(n);
      const cur = this.state[cfg.key];
      const curOpt = cfg.opts.find((opt) => opt.key === cur);
      const icoSvg = (o) => (o && o.ico ? `<svg class="ictf__opt-ico" viewBox="0 0 24 24" aria-hidden="true">${o.ico}</svg>` : '');
      const titleEl = stepEl.querySelector(`[data-ictf-step-label="${n}"]`);
      if (titleEl && n === 2) titleEl.textContent = 'STEP 02 - BRAND';

      if (n === 1) {
        const chips = stepEl.querySelector('[data-ictf-chips]');
        if (chips) {
          chips.innerHTML = cfg.opts.map((o) => `
            <button type="button" class="ictf__chip ictf__chip--ico" data-ictf-opt="${escapeHtml(o.key)}" aria-pressed="${o.key === cur ? 'true' : 'false'}">
              ${icoSvg(o)}${escapeHtml(o.label)}
            </button>
          `).join('');
          chips.querySelectorAll('[data-ictf-opt]').forEach((btn) => {
            btn.addEventListener('click', () => {
              this.setState({ vehicle: btn.dataset.ictfOpt, make: null, open: 2 });
            });
          });
        }
      }

      const labelEl = stepEl.querySelector(`[data-ictf-label="${n}"]`);
      if (labelEl) {
        labelEl.innerHTML = curOpt ? icoSvg(curOpt) + curOpt.label : cfg.ph;
        labelEl.classList.toggle('is-placeholder', !curOpt);
      }

      stepEl.classList.toggle('is-done', !!curOpt || (n === 1 && !!this.state.vehicle));
      stepEl.classList.toggle('is-open-step', this.state.open === n);
      const selectBtn = stepEl.querySelector(`[data-ictf-toggle="${n}"]`);
      if (selectBtn) selectBtn.classList.toggle('is-open', this.state.open === n);

      const dropdown = stepEl.querySelector(`[data-ictf-dropdown="${n}"]`);
      if (!dropdown) return;
      dropdown.hidden = this.state.open !== n;
      dropdown.innerHTML = (cfg.search
        ? '<div class="ictf__search-wrap"><input type="text" class="ictf__search" placeholder="Type to search brand..." aria-label="Search brand"></div>'
        : '') + cfg.opts.map((o) => `
          <button type="button" class="ictf__option${o.key === cur ? ' is-active' : ''}" data-ictf-opt="${escapeHtml(o.key)}" data-ictf-search="${escapeHtml(normalize(o.key))}">
            ${icoSvg(o)}${o.label}
          </button>
        `).join('');

      dropdown.querySelectorAll('[data-ictf-opt]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const patch = { open: null };
          if (cfg.key === 'vehicle') {
            patch.vehicle = btn.dataset.ictfOpt;
            patch.make = null;
            patch.open = 2;
          } else {
            patch.make = btn.dataset.ictfOpt;
          }
          this.setState(patch);
        });
      });

      const search = dropdown.querySelector('.ictf__search');
      if (search) {
        search.addEventListener('input', () => {
          const q = normalize(search.value);
          dropdown.querySelectorAll('[data-ictf-opt]').forEach((button) => {
            button.style.display = !q || button.dataset.ictfSearch.includes(q) ? '' : 'none';
          });
        });
        if (this.state.open === n) {
          setTimeout(() => {
            try { search.focus({ preventScroll: true }); } catch { search.focus(); }
          }, 0);
        }
      }
    }

    render() {
      this.renderStep(1);
      this.renderStep(2);

      const resultsEl = this.querySelector('[data-ictf-results]');
      if (!resultsEl) return;
      const matches = this.getMatches();
      this.selectedCompare = this.selectedCompare.filter((handle) => matches.includes(handle));
      resultsEl.hidden = matches.length === 0;
      const selectedMake = this.state.make || (this.state.vehicle === 'car' ? 'Popular picks' : 'Recommended picks');
      const countEl = this.querySelector('[data-ictf-count]');
      if (countEl) {
        countEl.textContent = `${matches.length} ${matches.length === 1 ? this.strings.fitOne : this.strings.fitMany} FOR ${selectedMake}`;
      }

      const grid = this.querySelector('[data-ictf-grid]');
      if (!grid) return;
      const firstRowCount = window.matchMedia && window.matchMedia('(min-width: 1200px)').matches ? 4 : 2;
      grid.innerHTML = matches.map((handle, i) => this.renderProductCard(handle, i, i < firstRowCount)).join('');

      this.bindCompareToggles(grid);
      this.renderShowcase(false);
      this.updateCompareTray();
      refreshCurrency();
    }

    toggleCompare(handle, checked) {
      if (!handle) return;
      if (checked) {
        if (!this.selectedCompare.includes(handle)) {
          if (this.selectedCompare.length >= 3) {
            const input = this.querySelector(`[data-ictf-compare-toggle][data-ictf-handle="${CSS.escape(handle)}"]`);
            if (input) input.checked = false;
            window.alert('Select up to 3 tools to compare.');
            return;
          }
          this.selectedCompare.push(handle);
        }
      } else {
        this.selectedCompare = this.selectedCompare.filter((item) => item !== handle);
      }
      this.updateCompareTray();
    }

    updateCompareTray() {
      if (!this.compareTray) return;
      const items = this.selectedCompare.map((handle) => this.products[handle]).filter(Boolean);
      this.compareTray.hidden = items.length === 0;
      if (this.compareCount) this.compareCount.textContent = String(items.length);
      if (this.compareOpen) this.compareOpen.disabled = items.length < 2;
      if (this.compareThumbs) {
        this.compareThumbs.innerHTML = items.map((item) => item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
          : '<span>Tool</span>').join('');
      }
      this.querySelectorAll('[data-ictf-compare-toggle]').forEach((input) => {
        input.checked = this.selectedCompare.includes(input.dataset.ictfHandle);
      });
    }

    clearCompare() {
      this.selectedCompare = [];
      this.updateCompareTray();
    }

    openCompare() {
      const items = this.selectedCompare.map((handle) => this.products[handle]).filter(Boolean);
      if (items.length < 2) {
        window.alert('Select at least 2 tools to compare.');
        return;
      }
      this.renderCompareTable(items);
      if (this.compareModal) {
        this.compareModal.hidden = false;
        document.documentElement.classList.add('ictf-compare-open');
      }
    }

    closeCompare() {
      if (this.compareModal) this.compareModal.hidden = true;
      document.documentElement.classList.remove('ictf-compare-open');
    }

    renderCompareTable(items) {
      if (!this.compareTable) return;
      const rows = compareRows(items);
      this.compareTable.innerHTML = `
        <table class="ictf__compare-table">
          <thead>
            <tr>
              <th>Parameter</th>
              ${items.map(compareHeader).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map(([label, getValue]) => `
              <tr>
                <th>${escapeHtml(label)}</th>
                ${items.map((item) => `<td>${getValue(item)}</td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
      this.compareTable.scrollLeft = 0;
      this.compareTable.scrollTop = 0;
      refreshCurrency();
    }
  }

  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest?.('[data-ictf-compare-open]');
    const closeBtn = event.target.closest?.('[data-ictf-compare-close]');
    const clearBtn = event.target.closest?.('[data-ictf-compare-clear]');

    if (!openBtn && !closeBtn && !clearBtn) return;

    const root = event.target.closest?.('icarsoft-tool-finder');
    if (!root) return;

    if (openBtn) {
      if (typeof root.openCompare === 'function') {
        root.openCompare();
        return;
      }

      let products = {};
      try {
        products = JSON.parse(root.querySelector('[data-ictf-products]')?.textContent || '{}');
      } catch {
        products = {};
      }

      const items = [...root.querySelectorAll('[data-ictf-compare-toggle]:checked')]
        .map((input) => products[input.dataset.ictfHandle])
        .filter(Boolean)
        .slice(0, 3);

      if (items.length < 2) {
        window.alert('Select at least 2 tools to compare.');
        return;
      }

      const table = root.querySelector('[data-ictf-compare-table]');
      const modal = root.querySelector('[data-ictf-compare-modal]');
      if (!table || !modal) return;

      const rows = compareRows(items);
      table.innerHTML = `
        <table class="ictf__compare-table">
          <thead>
            <tr>
              <th>Parameter</th>
              ${items.map(compareHeader).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map(([label, getValue]) => `
              <tr>
                <th>${escapeHtml(label)}</th>
                ${items.map((item) => `<td>${getValue(item)}</td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
      table.scrollLeft = 0;
      table.scrollTop = 0;
      modal.hidden = false;
      document.documentElement.classList.add('ictf-compare-open');
      refreshCurrency();
      return;
    }

    if (closeBtn) {
      root.querySelector('[data-ictf-compare-modal]')?.setAttribute('hidden', '');
      document.documentElement.classList.remove('ictf-compare-open');
      return;
    }

    if (clearBtn && typeof root.clearCompare !== 'function') {
      root.querySelectorAll('[data-ictf-compare-toggle]:checked').forEach((input) => {
        input.checked = false;
      });
      const tray = root.querySelector('[data-ictf-compare-tray]');
      if (tray) tray.hidden = true;
    }
  });

  window.addEventListener('BUCKSCC-LOADED', refreshCurrency);
  document.addEventListener('BUCKS_SELECTED_CURRENCY', refreshCurrency);
  window.addEventListener('BUCKS_SELECTED_CURRENCY', refreshCurrency);

  customElements.define('icarsoft-tool-finder', ICarsoftToolFinder);
})();
